import React, { useState, useEffect, useCallback } from "react";
import { Adjusting } from "../components/Trove/Adjusting";
import { Opening } from "../components/Trove/Opening";

import { Price } from "../components/Trove/PSY/Price";
import { CollateralStats } from "../components/Trove/PSY/CollateralStats";
import { Quotation } from "../components/Trove/PSY/Quotation";
import { useLiquitySelector } from "@liquity/lib-react"
import { Decimal, Percent, LiquityStoreState, Trove, SLSD_LIQUIDATION_RESERVE } from "@liquity/lib-base"
import { CollateralLexicons } from "src/lexicon";
import BackArrow from "src/assets/images/back_arrow.svg"
import { Link, Navigate, useParams } from "react-router-dom";
import { useAppState } from "../hooks/AppStateContext";

const feeFrom = (original: Trove, edited: Trove, borrowingRate: Decimal): Decimal => {
  const change = original.whatChanged(edited, borrowingRate);

  if (change && change.type !== "invalidCreation" && change.params.borrowSLSD) {
    return change.params.borrowSLSD.mul(borrowingRate);
  } else {
    return Decimal.ZERO;
  }
};

const EMPTY_TROVE = new Trove(Decimal.ZERO, Decimal.ZERO);

export const Product: React.FC = () => {
  const { collateralAddress } = useParams();
  const { updateAssetAddress, currentCollateralKey } = useAppState();
  useEffect(() => {
    updateAssetAddress(collateralAddress);
  }, [collateralAddress]);

  const selector = useCallback(({ collateralStates, collateralDerivedStates }: LiquityStoreState) => {
    if (!currentCollateralKey) return undefined

    const { price, total, collateralSurplusBalance } = collateralStates[currentCollateralKey];
    const { trove, fees } = collateralDerivedStates[currentCollateralKey];
    return { trove, fees, price, total, hasSurplusCollateral: !collateralSurplusBalance.isZero, recoveryMode: total.collateralRatioIsBelowCritical(price) }
  }, [currentCollateralKey]);

  const selectedState = useLiquitySelector(selector);

  const [collateral, setCollateral] = useState<Decimal>(selectedState ? selectedState.trove.collateral :  Decimal.ZERO);
  const [netDebt, setNetDebt] = useState<Decimal>(selectedState ? selectedState.trove.netDebt : Decimal.ZERO);

  useEffect(() => {
    if (!!selectedState) {
      setCollateral(selectedState.trove.collateral);
      setNetDebt(selectedState.trove.netDebt);
    }
  }, [currentCollateralKey]);

  if (!selectedState) return <></>;

  const { trove: userTrove, fees, price, total, hasSurplusCollateral, recoveryMode } = selectedState;
  const isAdjust = userTrove.status === "open"
  const trove = isAdjust ? userTrove : EMPTY_TROVE;

  const isCollateralIncrease = collateral.gt(trove.collateral);
  const isDebtIncrease = netDebt.gt(trove.netDebt);
  const debtIncreaseAmount = isDebtIncrease ? netDebt.sub(trove.netDebt) : Decimal.ZERO;
  const borrowingRate = fees.borrowingRate();

  const fee =
    isAdjust ? (
      isDebtIncrease
      ? feeFrom(trove, new Trove(trove.collateral, trove.debt.add(debtIncreaseAmount)), borrowingRate)
      : Decimal.ZERO
    ) : (
      netDebt.mul(borrowingRate)
    )

  const feePct = new Percent(borrowingRate);
  const totalDebt = netDebt.add(SLSD_LIQUIDATION_RESERVE).add(fee);

  const myCollateralRatio = collateral.mulDiv(price, totalDebt)

  const total1 = isCollateralIncrease ? total.addCollateral(collateral.sub(trove.collateral)) : total.subtractCollateral(trove.collateral.sub(collateral))
  const total2 = isDebtIncrease ? total1.addDebt(totalDebt.sub(trove.netDebt)) : total1.subtractDebt(trove.netDebt.sub(netDebt))
  const totalCollateralRatio = total2.collateralRatio(price)
  const lexicon = CollateralLexicons.find(lexicon => lexicon.key === currentCollateralKey)

  const hasNoTrove = userTrove.status === "nonExistent"
    || userTrove.status === "closedByLiquidation"
    || userTrove.status === "closedByOwner"
  return (
    <>
      {userTrove.status === "closedByLiquidation" && hasSurplusCollateral ? (
        <Navigate to="/" replace />
      ) : (
        <div className="w-full h-full md:mt-10 overflow-y-scroll">
          <div className="flex flex-row items-cener">
            <Link to="/products" className="flex flex-row">
              <img src={BackArrow} alt="back" className="mr-4" />
            </Link>
            <span className="text-style-title">Manage {lexicon?.term}</span>
            <img src={lexicon?.iconUrl} alt={lexicon?.term} className="ml-3 w-8 h-8" />
          </div>
          <div className="grid md:gid-rows-product md:grid-cols-product gap-5 w-full mt-8">
            {/* <div className="row-span-3 col-span-1"> */}
            <div className="flex flex-col">
            {/* <div > */}
              {userTrove.status === "open" && (
                <Adjusting
                  collateral={collateral}
                  setCollateral={setCollateral}
                  netDebt={netDebt}
                  setNetDebt={setNetDebt}
                  trove={trove}
                />
              )}
              {hasNoTrove && (
                <Opening
                  collateral={collateral}
                  setCollateral={setCollateral}
                  borrowAmount={netDebt}
                  setBorrowAmount={setNetDebt}
                  originalTrove={trove}
                />
              )}
            </div>
            <div className="flex flex-col gap-5">

              <div className="row-span-1 col-span-1">
                <Price
                  collateral={collateral}
                  debt={totalDebt}
                />
              </div>
              <div className="row-span-1 col-span-1">
                <CollateralStats
                  totalCollateralRatio={totalCollateralRatio}
                  myCollateralRatio={myCollateralRatio}
                  isRecoveryMode={recoveryMode}
                />
              </div>
              <div className="row-span-1 col-span-1">
                <Quotation
                  reserveFee={SLSD_LIQUIDATION_RESERVE}
                  borrowingFee={fee}
                  borrowingRate={`(${feePct.toString(2)})`}
                  totalDebt={totalDebt}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}